import { Theme } from "@mui/material";
import { keyframes } from "@mui/system";
import { SystemStyleObject } from "@mui/system";
import { SHEET_QUERY } from "global/components/AppModal/AppModal.tokens";
import { focusRing } from "theme/colors";
import tokens, {
  ReviewSeverityTone,
  ReviewStatusTone,
} from "./PresendReview.tokens";

const {
  color,
  countChip,
  document: doc,
  disclosure,
  card,
  notice,
  severity,
} = tokens;

type Sx = SystemStyleObject<Theme>;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

/** A reset `<button>` that reads as a text action -- the base of Dismiss, Show on page and Show. */
const textAction: Sx = {
  display: "inline-flex",
  alignItems: "center",
  p: 0,
  m: 0,
  border: 0,
  background: "none",
  cursor: "pointer",
  fontFamily: "inherit",
  "&:focus-visible": {
    outline: `2px solid ${focusRing}`,
    outlineOffset: "2px",
    borderRadius: "4px",
  },
};

/**
 * The document header's grid: tile, name, status pill (and the chevron, when the header opens and closes)
 * in one row. On a phone the pill drops under the name, so a long file name keeps the width.
 */
const headerGrid = (withChevron: boolean): Sx => ({
  display: "grid",
  alignItems: "center",
  columnGap: doc.gap,
  rowGap: doc.phoneRowGap,
  gridTemplateColumns: {
    xs: withChevron ? "auto minmax(0, 1fr) auto" : "auto minmax(0, 1fr)",
    sm: withChevron
      ? "auto minmax(0, 1fr) auto auto"
      : "auto minmax(0, 1fr) auto",
  },
  gridTemplateAreas: {
    xs: withChevron
      ? `"tile text chevron" "tile trailing chevron"`
      : `"tile text" "tile trailing"`,
    sm: withChevron ? `"tile text trailing chevron"` : `"tile text trailing"`,
  },
});

const uppercaseLabel = (fontSize: string, letterSpacing: string): Sx => ({
  fontSize,
  fontWeight: 700,
  letterSpacing,
  textTransform: "uppercase",
  lineHeight: "normal",
});

const styles = {
  eyebrow: {
    display: "block",
    mb: tokens.eyebrow.gap,
    color: color.muted,
    ...uppercaseLabel(tokens.eyebrow.fontSize, tokens.eyebrow.letterSpacing),
  } as Sx,

  spinner: {
    display: "flex",
    animation: `${spin} ${tokens.spinner.duration} linear infinite`,
  } as Sx,

  countRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: countChip.rowGap,
    pt: countChip.rowPaddingTop,
  } as Sx,

  countChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: countChip.gap,
    p: countChip.padding,
    borderRadius: countChip.radius,
    border: `1px solid ${color.border}`,
    bgcolor: color.card,
    color: color.ink,
    fontSize: countChip.fontSize,
    fontWeight: countChip.fontWeight,
    lineHeight: "normal",
    whiteSpace: "nowrap",
  } as Sx,

  countIcon: { display: "flex", color: color.sub } as Sx,

  dot: (tone: ReviewSeverityTone): Sx => ({
    width: countChip.dotSize,
    height: countChip.dotSize,
    borderRadius: "50%",
    flexShrink: 0,
    bgcolor: severity[tone].text,
  }),

  /** The documents, one after another, a hairline between each. */
  documents: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.body.betweenDocuments,
  } as Sx,

  documentDivider: {
    height: "1px",
    bgcolor: color.border,
    flexShrink: 0,
  } as Sx,

  document: {
    display: "flex",
    flexDirection: "column",
  } as Sx,

  /** Everything under the header. Its own top padding, so a closed document leaves no gap behind. */
  documentBody: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.body.documentGap,
    pt: tokens.body.documentGap,
  } as Sx,

  documentHeader: headerGrid(false),

  /** A header with nothing to open, among headers that open: keeps the chevron's column so pills line up. */
  documentHeaderAligned: headerGrid(true),

  chevronSpacer: {
    gridArea: "chevron",
    width: disclosure.chevronSize,
  } as Sx,

  /**
   * The header as the button that opens and closes the document. The hover wash and focus ring bleed past
   * the header's box; the negative margin keeps the layout where the plain header sits. It stays pinned at
   * the top of the scroll area while its own findings scroll under it (opaque, so they pass behind it);
   * the next document's header takes over once this document has scrolled by.
   */
  documentToggle: (pinned: boolean): Sx => ({
    ...headerGrid(true),
    position: "sticky",
    top: disclosure.stickyTop,
    [SHEET_QUERY]: { top: disclosure.sheetStickyTop },
    zIndex: 1,
    boxSizing: "border-box",
    width: `calc(100% + ${disclosure.bleedX * 2}px)`,
    m: `-${disclosure.bleedY}px -${disclosure.bleedX}px`,
    p: `${disclosure.bleedY}px ${disclosure.bleedX}px`,
    border: 0,
    borderRadius: pinned ? 0 : disclosure.radius,
    background: color.card,
    // Pinned: a hairline marks where the findings pass under it.
    boxShadow: pinned ? `0 1px 0 ${color.border}` : "none",
    color: "inherit",
    font: "inherit",
    textAlign: "left",
    cursor: "pointer",
    "&:hover": { bgcolor: color.hover },
    "&:focus-visible": {
      outline: `2px solid ${focusRing}`,
      outlineOffset: 0,
    },
  }),

  documentTrailing: {
    gridArea: "trailing",
    display: "flex",
    justifySelf: { xs: "start", sm: "end" },
  } as Sx,

  chevron: (expanded: boolean): Sx => ({
    gridArea: "chevron",
    display: "flex",
    color: color.sub,
    transition: `transform ${disclosure.duration} ease`,
    transform: expanded ? "rotate(180deg)" : "none",
  }),

  documentTile: {
    gridArea: "tile",
    alignSelf: { xs: "start", sm: "center" },
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    p: doc.tilePadding,
    borderRadius: doc.tileRadius,
    bgcolor: color.surface,
    color: color.body,
  } as Sx,

  documentText: {
    gridArea: "text",
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: "1px",
  } as Sx,

  documentName: {
    m: 0,
    color: color.ink,
    fontSize: doc.nameSize,
    fontWeight: doc.nameWeight,
    lineHeight: doc.nameLineHeight,
    overflowWrap: "anywhere",
  } as Sx,

  documentMeta: {
    m: 0,
    color: color.muted,
    fontSize: doc.metaSize,
    lineHeight: doc.metaLineHeight,
  } as Sx,

  checking: {
    flexShrink: 0,
    color: color.muted,
    fontSize: doc.checkingSize,
    fontWeight: doc.checkingWeight,
    whiteSpace: "nowrap",
  } as Sx,

  statusPill: (tone: ReviewStatusTone): Sx => ({
    display: "inline-flex",
    alignItems: "center",
    flexShrink: 0,
    gap: tokens.statusPill.gap,
    p: tokens.statusPill.padding,
    borderRadius: tokens.statusPill.radius,
    bgcolor: tokens.statusPill[tone].bg,
    color: tokens.statusPill[tone].text,
    fontSize: tokens.statusPill.fontSize,
    fontWeight: tokens.statusPill.fontWeight,
    lineHeight: "normal",
    whiteSpace: "nowrap",
  }),

  /** "REQUIRED FIELDS ─────" */
  categoryHeading: {
    display: "flex",
    alignItems: "center",
    gap: tokens.categoryHeading.gap,
    minHeight: tokens.categoryHeading.height,
    m: 0,
    color: color.sub,
    ...uppercaseLabel(
      tokens.categoryHeading.fontSize,
      tokens.categoryHeading.letterSpacing,
    ),
    fontWeight: tokens.categoryHeading.fontWeight,
    "&::after": {
      content: '""',
      flex: 1,
      height: "1px",
      bgcolor: color.border,
    },
  } as Sx,

  card: (tone: ReviewSeverityTone): Sx => ({
    display: "flex",
    flexDirection: "column",
    gap: card.gap,
    m: 0,
    p: card.padding,
    listStyle: "none",
    borderRadius: card.radius,
    border: `1px solid ${color.border}`,
    // The design's accent is a 3px strip inside the card's rounded edge.
    boxShadow: `inset ${card.accentWidth} 0 0 ${severity[tone].accent}`,
    bgcolor: color.card,
    boxSizing: "border-box",
  }),

  cardTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
  } as Sx,

  severityChip: (tone: ReviewSeverityTone): Sx => ({
    display: "inline-flex",
    alignItems: "center",
    gap: tokens.severityChip.gap,
    p: tokens.severityChip.padding,
    borderRadius: tokens.severityChip.radius,
    bgcolor: severity[tone].bg,
    color: severity[tone].text,
    fontSize: tokens.severityChip.fontSize,
    fontWeight: tokens.severityChip.fontWeight,
    lineHeight: "normal",
    whiteSpace: "nowrap",
    "&::before": {
      content: '""',
      width: tokens.severityChip.dotSize,
      height: tokens.severityChip.dotSize,
      borderRadius: "50%",
      bgcolor: severity[tone].text,
    },
  }),

  dismiss: {
    ...textAction,
    gap: card.dismissGap,
    color: color.muted,
    fontSize: card.actionSize,
    fontWeight: card.actionWeight,
    "&:hover": { color: color.sub },
  } as Sx,

  cardTitle: {
    m: 0,
    color: color.ink,
    fontSize: card.titleSize,
    fontWeight: card.titleWeight,
    lineHeight: card.titleLineHeight,
    overflowWrap: "anywhere",
  } as Sx,

  cardDetail: {
    m: 0,
    color: color.body,
    fontSize: card.detailSize,
    lineHeight: card.detailLineHeight,
    overflowWrap: "anywhere",
  } as Sx,

  snippet: {
    m: 0,
    p: card.snippet.padding,
    borderRadius: card.snippet.radius,
    bgcolor: color.surface,
    color: color.sub,
    fontSize: card.snippet.fontSize,
    lineHeight: card.snippet.lineHeight,
    overflowWrap: "anywhere",
  } as Sx,

  whatToDo: {
    display: "flex",
    flexDirection: "column",
    gap: card.whatToDoGap,
  } as Sx,

  whatToDoLabel: {
    m: 0,
    color: color.muted,
    ...uppercaseLabel(card.whatToDoSize, card.whatToDoSpacing),
  } as Sx,

  suggestion: {
    m: 0,
    color: color.ink,
    fontSize: card.suggestionSize,
    fontWeight: card.suggestionWeight,
    lineHeight: card.suggestionLineHeight,
    overflowWrap: "anywhere",
  } as Sx,

  showOnPage: {
    ...textAction,
    alignSelf: "flex-start",
    gap: card.locationGap,
    color: color.link,
    fontSize: card.actionSize,
    fontWeight: card.actionWeight,
    "&:hover": { textDecoration: "underline" },
  } as Sx,

  /** A `not_checked` notice: a tinted well with the struck-through eye. */
  notCheckedRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: notice.gap,
    m: 0,
    p: notice.padding,
    listStyle: "none",
    borderRadius: notice.radius,
    bgcolor: color.surface,
  } as Sx,

  notCheckedIcon: { display: "flex", pt: "1px", color: color.sub } as Sx,

  noticeText: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: notice.textGap,
  } as Sx,

  noticeTitle: {
    m: 0,
    color: color.ink,
    fontSize: notice.titleSize,
    fontWeight: notice.titleWeight,
    lineHeight: notice.titleLineHeight,
    overflowWrap: "anywhere",
  } as Sx,

  noticeDetail: {
    m: 0,
    color: color.sub,
    fontSize: notice.detailSize,
    lineHeight: notice.detailLineHeight,
    overflowWrap: "anywhere",
  } as Sx,

  /** An `info` notice: one quiet line, no box. */
  infoRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: notice.infoGap,
    color: color.sub,
    fontSize: notice.detailSize,
    lineHeight: notice.detailLineHeight,
  } as Sx,

  infoIcon: { display: "flex", pt: "2px", color: color.muted } as Sx,

  list: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.body.documentGap,
    m: 0,
    p: 0,
  } as Sx,

  skeletonCard: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.skeleton.gap,
    p: tokens.skeleton.padding,
    borderRadius: card.radius,
    border: `1px solid ${color.border}`,
    bgcolor: color.card,
  } as Sx,

  skeletonBar: (bar: { width: number; height: number }): Sx => ({
    width: bar.width,
    maxWidth: "100%",
    height: bar.height,
    borderRadius: tokens.skeleton.radius,
    bgcolor: color.surface,
  }),

  footerNote: {
    m: 0,
    color: color.muted,
    fontSize: tokens.footer.noteSize,
    lineHeight: tokens.footer.noteLineHeight,
  } as Sx,

  footerLink: {
    ...textAction,
    color: color.sub,
    fontSize: tokens.footer.noteSize,
    fontWeight: 600,
    textDecoration: "underline",
  } as Sx,

  /**
   * "Try again": a brand-red outlined secondary, as the editor header's "Previous" draws it. The theme's
   * `outlined` slot writes the `border` shorthand, so the shorthand is restated one step higher.
   */
  tryAgainButton: {
    "&.MuiButton-root": {
      border: `1px solid ${color.link}`,
      color: color.link,
      "&:hover": { border: `1px solid ${color.link}`, color: color.link },
    },
  } as Sx,
};

export default styles;
